/** Shared by every Artwork image upload, including uploads saved in Drive. */
export const MAX_IMAGE_EDGE = 4096;
export const MAX_IMAGE_BYTES = 64 * 1024 * 1024;

interface ImageSize { width: number; height: number }
export interface PreparedImage extends ImageSize {
  blob: Blob;
  downsizedFrom?: ImageSize;
}

/** Reject before opening a potentially large file, including SVG imports. */
export function checkImageSize(file: File): void {
  if (file.size > MAX_IMAGE_BYTES) throw new Error(`${file.name || 'That image'} is over 64 MiB. Choose a smaller image.`);
}

/** A notice describes the bytes actually saved, rather than guessing from the input. */
export function downsizingNotice(pictures: readonly (ImageSize & { name: string; downsizedFrom?: ImageSize })[]): string | null {
  const changed = pictures.filter(picture => picture.downsizedFrom);
  const first = changed[0];
  if (!first) return null;
  if (changed.length > 1) return `${changed.length} images were downsized to at most ${MAX_IMAGE_EDGE.toLocaleString('en-US')} px on the longest edge for faster editing. The uploaded copies use this resolution.`;
  const from = first.downsizedFrom!;
  return `${first.name} was downsized from ${from.width} × ${from.height} to ${first.width} × ${first.height} px for faster editing. The uploaded copy uses this resolution.`;
}

/** Avoid flattening an animation when a still-image resize would lose its frames. */
async function animated(file: File): Promise<boolean> {
  if (file.type === 'image/gif') return true;
  if (file.type === 'image/webp') {
    const bytes = new Uint8Array(await file.slice(0, 21).arrayBuffer());
    return String.fromCharCode(...bytes.slice(12, 16)) === 'VP8X' && !!((bytes[20] ?? 0) & 2);
  }
  if (file.type === 'image/avif') {
    const header = new TextDecoder().decode(await file.slice(0, 64).arrayBuffer());
    return header.includes('avis');
  }
  if (file.type === 'image/png') {
    // APNG's animation control chunk precedes its first image-data chunk. Skip payloads.
    let offset = 8;
    while (offset + 8 <= file.size) {
      const bytes = new Uint8Array(await file.slice(offset, offset + 8).arrayBuffer());
      const type = String.fromCharCode(...bytes.slice(4, 8));
      if (type === 'acTL') return true;
      if (type === 'IDAT' || type === 'IEND') return false;
      offset += new DataView(bytes.buffer).getUint32(0) + 12;
    }
  }
  return false;
}

/**
 * Resize before upload, preserving aspect ratio and transparency. Smaller supported
 * images retain their original bytes. SVG/AVIF are converted for the canvas decoder.
 * A failed resize is refused: uploading the full-size image would defeat the cap.
 */
export async function prepareImage(file: File): Promise<PreparedImage> {
  checkImageSize(file);
  const localUrl = URL.createObjectURL(file);
  let canvas: HTMLCanvasElement | undefined;
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('That file isn’t a picture Artwork can use. Try a PNG, JPEG, WebP, GIF or SVG.'));
      image.src = localUrl;
    });
    const size = { width: image.naturalWidth, height: image.naturalHeight };
    if (!(size.width > 0 && size.height > 0)) throw new Error('That image has no readable dimensions.');
    const longest = Math.max(size.width, size.height);
    const vector = file.type === 'image/svg+xml';
    const redraw = vector || file.type === 'image/avif';
    if (longest <= MAX_IMAGE_EDGE && !redraw) return { blob: file, ...size };
    if (await animated(file)) {
      if (file.type === 'image/avif') throw new Error('Animated AVIF cannot be converted without losing its animation. Try a GIF, animated WebP or video.');
      throw new Error(`${file.name || 'That animation'} cannot be resized without losing its animation. Use an animation no larger than ${MAX_IMAGE_EDGE.toLocaleString('en-US')} px on the longest edge.`);
    }
    const scale = vector ? Math.min(MAX_IMAGE_EDGE, Math.max(1600, longest)) / longest : Math.min(1, MAX_IMAGE_EDGE / longest);
    const width = Math.max(1, Math.round(size.width * scale));
    const height = Math.max(1, Math.round(size.height * scale));
    canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('That image could not be resized. Try a smaller image.');
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, width, height);
    const blob = await new Promise<Blob | null>(resolve => canvas!.toBlob(resolve, vector ? 'image/png' : 'image/webp', 0.9));
    if (!blob) throw new Error('That image could not be resized. Try a smaller image.');
    if (blob.size > MAX_IMAGE_BYTES) throw new Error('That image is still too large after resizing. Try a smaller image.');
    return { blob, width, height, ...(scale < 1 ? { downsizedFrom: size } : {}) };
  } finally {
    URL.revokeObjectURL(localUrl);
    if (canvas) { canvas.width = 0; canvas.height = 0; }
  }
}
